// Service để parse và xử lý lệnh giọng nói

/**
 * Extract số giây từ lệnh countdown
 * Ví dụ: "sau 5 giây" -> 5, "sau 30 giây" -> 30, "sau 1 phút" -> 60
 */
function extractCountdownSeconds(command) {
  const lowerCommand = command.toLowerCase();
  
  // Pattern: "sau X giây" hoặc "sau X phút" hoặc "sau X giờ"
  const countdownPatterns = [
    /sau\s+(\d+)\s*giây/i,
    /sau\s+(\d+)\s*phút/i,
    /sau\s+(\d+)\s*giờ/i,
    /(\d+)\s*giây\s+sau/i,
    /(\d+)\s*phút\s+sau/i,
    /(\d+)\s*giờ\s+sau/i
  ];
  
  for (const pattern of countdownPatterns) {
    const match = lowerCommand.match(pattern);
    if (match) {
      const number = parseInt(match[1]);
      if (pattern.source.includes('phút')) {
        return number * 60; // Chuyển phút sang giây
      } else if (pattern.source.includes('giờ')) {
        return number * 3600; // Chuyển giờ sang giây
      } else {
        return number; // Giây
      }
    }
  }
  
  return null;
}

/**
 * Extract thời gian từ lệnh giọng nói
 * Ví dụ: "hẹn giờ bật lúc 10 giờ 30" -> { hours: 10, minutes: 30 }
 * "bật lúc 8 giờ sáng" -> { hours: 8, minutes: 0 }
 * "tắt lúc 22 giờ" -> { hours: 22, minutes: 0 }
 */
function extractTime(command) {
  const lowerCommand = command.toLowerCase();
  
  // Pattern: "lúc X giờ Y phút" hoặc "lúc X:Y"
  // QUAN TRỌNG: Pattern có phút phải đứng trước pattern chỉ có giờ để tránh match sai
  const timePatterns = [
    /lúc\s+(\d{1,2})\s*giờ\s*(\d{1,2})\s*phút/i,
    /(\d{1,2})\s*giờ\s*(\d{1,2})\s*phút/i,  // "7 giờ 38 phút" hoặc "7 giờ 38 chiều"
    /lúc\s+(\d{1,2}):(\d{1,2})/i,
    /(\d{1,2}):(\d{1,2})/i,  // "7:38"
    /lúc\s+(\d{1,2})\s*giờ/i,
    /(\d{1,2})\s*giờ/i  // Chỉ có giờ, không có phút
  ];
  
  for (const pattern of timePatterns) {
    const match = lowerCommand.match(pattern);
    if (match) {
      let hours = parseInt(match[1] || match[2] || 0);
      let minutes = parseInt(match[2] || match[3] || 0);
      
      // Nếu hours >= 12, đã là format 24h → bỏ qua xử lý "chiều"/"sáng"
      // Chỉ xử lý AM/PM khi hours < 12 (format 12h)
      if (hours < 12) {
        // Xử lý AM/PM cho format 12h
        if (lowerCommand.includes('sáng') || lowerCommand.includes('am')) {
          // "12 giờ sáng" = 00:00 (nửa đêm)
          if (hours === 12) hours = 0;
          // Các giờ khác giữ nguyên (1-11 giờ sáng)
        } else if (lowerCommand.includes('chiều') || lowerCommand.includes('tối') || lowerCommand.includes('pm')) {
          // "X giờ chiều" = X + 12 (1-11 giờ chiều)
          hours += 12;
        }
      }
      // Nếu hours >= 12, giữ nguyên (đã là format 24h)
      
      // Validate
      if (hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59) {
        return { hours, minutes };
      }
    }
  }
  
  return null;
}

/**
 * Tạo ISO datetime string từ hours và minutes (cho hôm nay hoặc ngày mai nếu đã qua)
 */
function createScheduledDateTime(hours, minutes) {
  const now = new Date();
  const scheduled = new Date();
  scheduled.setHours(hours, minutes, 0, 0);
  
  // Nếu thời gian đã qua hôm nay, đặt cho ngày mai
  if (scheduled <= now) {
    scheduled.setDate(scheduled.getDate() + 1);
  }
  
  // Format: "2026-01-03T10:30:00"
  return scheduled.toISOString().slice(0, 19);
}

class VoiceCommandHandler {
  /**
   * Parse lệnh giọng nói và trả về action
   * @param {string} command - Lệnh giọng nói
   * @returns {Object|null} - Action object hoặc null nếu không phải lệnh điều khiển
   */
  static parseCommand(command) {
    if (!command) return null;

    const lowerCommand = command.toLowerCase().trim();

    // Lệnh countdown (phải kiểm tra trước device_control để tránh nhầm lẫn)
    const countdownSeconds = extractCountdownSeconds(lowerCommand);
    if (countdownSeconds !== null) {
      // Kiểm tra xem có phải lệnh bật/tắt với countdown không
      if (lowerCommand.includes('bật') || lowerCommand.includes('mở')) {
        return {
          type: 'countdown',
          device: 'smart_socket',
          action: 'countdown_on',
          seconds: countdownSeconds,
          originalCommand: command
        };
      } else if (lowerCommand.includes('tắt') || lowerCommand.includes('đóng')) {
        return {
          type: 'countdown',
          device: 'smart_socket',
          action: 'countdown_off',
          seconds: countdownSeconds,
          originalCommand: command
        };
      }
    }

    // Lệnh hẹn giờ (Schedule) - PHẢI KIỂM TRA TRƯỚC device_control
    // Nếu có thời gian (lúc X giờ Y phút), đó là schedule, không phải bật/tắt ngay
    const scheduledTime = extractTime(lowerCommand);
    if (scheduledTime !== null) {
      // Có thời gian, kiểm tra xem có phải lệnh bật/tắt với schedule không
      // Kiểm tra cả "ổ cắm" để đảm bảo đây là lệnh điều khiển ổ cắm, không phải thiết bị khác
      const hasSocketKeyword = lowerCommand.includes('ổ cắm') || lowerCommand.includes('ô cắm') || lowerCommand.includes('socket');
      
      if (hasSocketKeyword) {
        if (lowerCommand.includes('bật') || lowerCommand.includes('mở') || lowerCommand.includes('hẹn giờ bật') || lowerCommand.includes('hẹn bật')) {
          return {
            type: 'schedule',
            device: 'smart_socket',
            action: 'schedule_on',
            scheduledTime: scheduledTime,
            originalCommand: command
          };
        } else if (lowerCommand.includes('tắt') || lowerCommand.includes('đóng') || lowerCommand.includes('hẹn giờ tắt') || lowerCommand.includes('hẹn tắt')) {
          return {
            type: 'schedule',
            device: 'smart_socket',
            action: 'schedule_off',
            scheduledTime: scheduledTime,
            originalCommand: command
          };
        }
      }
    }

    // Lệnh hỏi trạng thái
    const statusPatterns = [
      'trạng thái ổ cắm', 'trạng thái ô cắm', 'ổ cắm thế nào', 'ô cắm thế nào',
      'công suất ổ cắm', 'công suất ô cắm', 'ổ cắm bao nhiêu watt',
      'status socket', 'socket status'
    ];

    for (const pattern of statusPatterns) {
      if (lowerCommand.includes(pattern)) {
        return {
          type: 'device_status',
          device: 'smart_socket',
          action: 'get_status',
          originalCommand: command
        };
      }
    }

    // Lệnh điều khiển Smart Socket (Ổ cắm) - chỉ khi KHÔNG có countdown và KHÔNG có schedule
    const socketPatterns = {
      turnOn: [
        'bật ổ cắm', 'bật ô cắm', 'bật ổ cắm thông minh', 'bật ô cắm thông minh',
        'mở ổ cắm', 'mở ô cắm', 'mở ổ cắm thông minh',
        'bật smart socket', 'turn on socket', 'bật socket'
      ],
      turnOff: [
        'tắt ổ cắm', 'tắt ô cắm', 'tắt ổ cắm thông minh', 'tắt ô cắm thông minh',
        'đóng ổ cắm', 'đóng ô cắm', 'đóng ổ cắm thông minh',
        'tắt smart socket', 'turn off socket', 'tắt socket'
      ]
    };

    // Kiểm tra lệnh bật
    for (const pattern of socketPatterns.turnOn) {
      if (lowerCommand.includes(pattern)) {
        return {
          type: 'device_control',
          device: 'smart_socket',
          action: 'turn_on',
          originalCommand: command
        };
      }
    }

    // Kiểm tra lệnh tắt
    for (const pattern of socketPatterns.turnOff) {
      if (lowerCommand.includes(pattern)) {
        return {
          type: 'device_control',
          device: 'smart_socket',
          action: 'turn_off',
          originalCommand: command
        };
      }
    }

    // Không phải lệnh điều khiển, trả về null để gửi cho AI
    return null;
  }

  /**
   * Tạo phản hồi cho lệnh điều khiển
   * @param {Object} command - Command object từ parseCommand
   * @param {Object} result - Kết quả từ device service
   * @returns {string} - Phản hồi bằng văn bản
   */
  static generateResponse(command, result = null) {
    if (!command) return null;

    switch (command.action) {
      case 'turn_on':
        if (result && result.success) {
          return ' Đã bật ổ cắm thông minh thành công!';
        } else if (result && !result.success) {
          return `❌ ${result.message || 'Không thể bật ổ cắm'}`;
        }
        return 'Đang bật ổ cắm thông minh...';

      case 'turn_off':
        if (result && result.success) {
          return ' Đã tắt ổ cắm thông minh thành công!';
        } else if (result && !result.success) {
          return `❌ ${result.message || 'Không thể tắt ổ cắm'}`;
        }
        return 'Đang tắt ổ cắm thông minh...';

      case 'get_status':
        if (result) {
          const status = result.isOn ? 'đang bật' : 'đang tắt';
          const power = result.power ? `, công suất: ${result.power.toFixed(2)}W` : '';
          return ` Ổ cắm thông minh ${status}${power}`;
        }
        return 'Đang kiểm tra trạng thái ổ cắm...';

      case 'schedule_on':
      case 'schedule_off':
        if (result && result.success) {
          const action = command.action === 'schedule_on' ? 'bật' : 'tắt';
          const time = command.scheduledTime;
          const timeStr = `${time.hours.toString().padStart(2, '0')}:${time.minutes.toString().padStart(2, '0')}`;
          return `✅ Đã hẹn giờ ${action} ổ cắm lúc ${timeStr}`;
        } else if (result && !result.success) {
          return `❌ ${result.message || 'Không thể tạo lịch hẹn giờ'}`;
        }
        return 'Đang tạo lịch hẹn giờ...';

      case 'countdown_on':
      case 'countdown_off':
        if (result && result.success) {
          const action = command.action === 'countdown_on' ? 'bật' : 'tắt';
          const seconds = command.seconds;
          const timeStr = seconds < 60 ? `${seconds} giây` : seconds < 3600 ? `${Math.floor(seconds / 60)} phút` : `${Math.floor(seconds / 3600)} giờ`;
          return `✅ Đã hẹn giờ ${action} ổ cắm sau ${timeStr}`;
        } else if (result && !result.success) {
          return `❌ ${result.message || 'Không thể tạo hẹn giờ nhanh'}`;
        }
        return 'Đang tạo hẹn giờ nhanh...';

      default:
        return null;
    }
  }
}

export default VoiceCommandHandler;




